<template>
  <div class="nx-page nx-report-page customer-transport-portrait">
    <!-- 筛选 -->
    <div class="nx-report-header portrait-header">
      <el-form
        ref="filterForm"
        :inline="true"
        size="small"
        class="filter-bar"
      >
        <el-form-item label="选择客户">
          <el-select
            v-model="query.custNo"
            clearable
            filterable
            remote
            reserve-keyword
            placeholder="请输入客户关键词"
            :loading="selectLoading"
            :remote-method="remoteMethod"
            style="min-width: 220px"
            @change="handleCustomerChange"
          >
            <el-option
              v-for="item in custList"
              :key="item.id"
              :label="item.deptName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="统计年份" class="date-item">
          <el-date-picker
            v-model="query.seachTime"
            type="year"
            placeholder="选择统计年份"
            value-format="yyyy"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-refresh" @click="handleRefresh">
            刷新画像
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-skeleton :loading="pageLoading" animated>
      <template #template>
        <el-row :gutter="16">
          <el-col :span="24">
            <el-skeleton-item variant="rect" style="height: 120px" />
          </el-col>
        </el-row>
      </template>

      <template #default>
        <!-- 客户基础信息 -->
        <section class="nx-section-card base-info-card fade-card">
          <div class="section-header">
            <div class="section-title">客户基础信息</div>
          </div>
          <el-row :gutter="16" class="info-row">
            <el-col :md="8" :sm="12" :xs="24">
              <div class="info-item">
                <div class="info-label">客户名称</div>
                <div class="info-value">{{ portrait.customerName || '--' }}</div>
              </div>
            </el-col>
            <el-col :md="8" :sm="12" :xs="24">
              <div class="info-item">
                <div class="info-label">统一社会信用代码</div>
                <div class="info-value">{{ portrait.creditCode || '--' }}</div>
              </div>
            </el-col>
            <el-col :md="4" :sm="12" :xs="24">
              <div class="info-item">
                <div class="info-label">联系人</div>
                <div class="info-value">{{ portrait.contactPerson || '--' }}</div>
              </div>
            </el-col>
            <el-col :md="4" :sm="12" :xs="24">
              <div class="info-item">
                <div class="info-label">联系方式</div>
                <div class="info-value">{{ portrait.contactPhone || '--' }}</div>
              </div>
            </el-col>
          </el-row>
        </section>

        <!-- 关键指标 -->
        <section class="kpi-section fade-card">
          <div class="nx-kpi-grid">
            <div class="nx-kpi-card" v-for="item in kpiList" :key="item.key">
              <div class="kpi-label">{{ item.label }}</div>
              <div class="kpi-value">
                {{ item.formatter ? item.formatter(portrait[item.key]) : (portrait[item.key] || portrait[item.key] === 0 ? portrait[item.key] : '--') }}
              </div>
            </div>
          </div>
        </section>

        <!-- 合同执行全景 -->
        <section class="nx-section-card contract-section fade-card">
          <div class="section-header">
            <div class="section-title">合同执行全景</div>
          </div>
          <el-row :gutter="24">
            <el-col :md="10" :sm="24">
              <el-row :gutter="16" class="info-row">
                <el-col :span="8">
                  <div class="info-item">
                    <div class="info-label">年度合同总量(吨)</div>
                    <div class="info-value">{{ formatNumber(portrait.contractAmount) }}</div>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="info-item">
                    <div class="info-label">已完成(吨)</div>
                    <div class="info-value">{{ formatNumber(portrait.completedAmount) }}</div>
                  </div>
                </el-col>
                <el-col :span="8">
                  <div class="info-item">
                    <div class="info-label">剩余合同量(吨)</div>
                    <div class="info-value">
                      {{ formatNumber(portrait.contractAmount - portrait.completedAmount) }}
                    </div>
                  </div>
                </el-col>
              </el-row>
              <div class="progress-bar-container">
                <div
                  class="progress-fill"
                  :style="{ width: contractPercent + '%' }"
                >
                  {{ contractPercent }}%
                </div>
              </div>
              <div class="sub-title-note">基于汽运业务全量合同执行情况统计</div>
            </el-col>

            <el-col :md="8" :sm="24">
              <div class="sub-card-title">各煤种合同执行</div>
              <el-table
                :data="portrait.coalDetails"
                border
                size="mini"
                style="width: 100%"
                empty-text="暂无数据"
              >
                <el-table-column prop="name" label="煤种" />
                <el-table-column prop="plan" label="合同量(吨)" :formatter="(r, c, v) => formatNumber(v)" />
                <el-table-column prop="actual" label="已完成(吨)" :formatter="(r, c, v) => formatNumber(v)" />
                <el-table-column
                  label="完成率"
                  :formatter="(_, __, ___, idx) => formatPercent(portrait.coalDetails[idx])"
                />
              </el-table>
            </el-col>

            <el-col :md="6" :sm="24">
              <div class="sub-card-title">各费目合同金额（万元）</div>
              <div ref="feeChart" class="chart-card" style="height: 220px" />
            </el-col>
          </el-row>
        </section>

        <!-- 运输与库存效率 -->
        <section class="nx-section-card transport-section fade-card">
          <div class="section-header">
            <div class="section-title">运输与库存效率</div>
          </div>
          <el-row :gutter="24">
            <el-col :md="13" :sm="24">
              <div class="sub-card-title">运输行为特征</div>
              <el-row :gutter="16" class="info-row">
                <el-col :md="6" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">月发运频次(车次)</div>
                    <div class="info-value">{{ portrait.monthlyFreq || '--' }}</div>
                  </div>
                </el-col>
                <el-col :md="6" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">常用煤种</div>
                    <div class="info-value">{{ portrait.commonCoal || '--' }}</div>
                  </div>
                </el-col>
                <el-col :md="6" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">平均停留时长(分钟)</div>
                    <div class="info-value">{{ portrait.avgStayTime || '--' }}</div>
                  </div>
                </el-col>
                <el-col :md="6" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">异常预警次数</div>
                    <div class="info-value">{{ (portrait.warnings && portrait.warnings.length) || 0 }}</div>
                  </div>
                </el-col>
              </el-row>

              <div ref="monthlyTrendChart" class="chart-card" style="height: 280px; margin-top: 8px" />
            </el-col>

            <el-col :md="11" :sm="24">
              <div class="sub-card-title">库存与周转效率</div>
              <el-row :gutter="16" class="info-row">
                <el-col :md="8" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">当前库存(吨)</div>
                    <div class="info-value">{{ formatNumber(portrait.currentStock) }}</div>
                  </div>
                </el-col>
                <el-col :md="8" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">库存周转率(次/年)</div>
                    <div class="info-value">{{ portrait.turnoverRate || '--' }}</div>
                  </div>
                </el-col>
                <el-col :md="8" :sm="12" :xs="12">
                  <div class="info-item">
                    <div class="info-label">周转天数</div>
                    <div class="info-value">{{ portrait.turnoverDays || '--' }}</div>
                  </div>
                </el-col>
              </el-row>

              <div ref="transportCompareChart" class="chart-card" style="height: 280px; margin-top: 8px" />
            </el-col>
          </el-row>
        </section>

        <!-- 客户价值分层 + 煤种偏好 -->
        <section class="value-section fade-card-group">
          <el-row :gutter="24">
            <el-col :md="12" :sm="24">
              <div class="nx-section-card fade-card">
                <div class="section-header">
                  <div class="section-title">客户价值分层</div>
                </div>
                <el-row :gutter="16" class="info-row">
                  <el-col :md="8" :sm="12" :xs="12">
                    <div class="info-item">
                      <div class="info-label">客户等级</div>
                      <div class="info-value">
                        <span class="level-badge" :class="levelClass">{{ portrait.level || '--' }}</span>
                      </div>
                    </div>
                  </el-col>
                  <el-col :md="8" :sm="12" :xs="12">
                    <div class="info-item">
                      <div class="info-label">信用额度(万元)</div>
                      <div class="info-value">{{ portrait.creditLimit || '--' }}</div>
                    </div>
                  </el-col>
                  <el-col :md="8" :sm="12" :xs="12">
                    <div class="info-item">
                      <div class="info-label">预付款余额(万元)</div>
                      <div class="info-value">{{ portrait.prepayBalance || '--' }}</div>
                    </div>
                  </el-col>
                </el-row>
                <div class="info-item reason-item">
                  <div class="info-label">评价原因</div>
                  <div class="info-value">{{ portrait.levelReason || '--' }}</div>
                </div>
              </div>
            </el-col>

            <el-col :md="12" :sm="24">
              <div class="nx-section-card fade-card">
                <div class="section-header">
                  <div class="section-title">煤种偏好</div>
                </div>
                <div ref="coalPreferenceChart" class="chart-card" style="height: 260px" />
                <div class="note">按到货量占比统计</div>
              </div>
            </el-col>
          </el-row>
        </section>

        <!-- 异常预警记录 -->
        <section class="nx-section-card warning-section fade-card">
          <div class="section-header">
            <div class="section-title">异常预警记录（近30天）</div>
          </div>
          <el-table
            :data="portrait.warnings || []"
            border
            size="mini"
            style="width: 100%"
            empty-text="暂无异常记录"
          >
            <el-table-column prop="plate" label="车牌号" />
            <el-table-column prop="driver" label="司机" />
            <el-table-column prop="stayMinutes" label="停留时长(分钟)" width="130" />
            <el-table-column prop="time" label="发生时间" width="180" />
            <el-table-column label="预警原因">
              <template slot-scope="scope">
                <span class="level-badge level-high">{{ scope.row.reason }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div class="note">* 停留时长超过 60 分钟触发预警</div>
        </section>
      </template>
    </el-skeleton>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getRequset } from '@/basic-common/util/common'
import { getCustomerPortrait } from './api'

export default {
  name: 'AutomobileCustomerTransportPortrait',
  data() {
    return {
      pageLoading: false,
      selectLoading: false,
      query: {
        custNo: '',
        seachTime: String(new Date().getFullYear()),
        startTime: `${new Date().getFullYear()}-01-01 00:00:00`,
        endTime: `${new Date().getFullYear()}-12-31 23:59:59`
      },
      custList: [],
      portrait: {
        customerName: '',
        creditCode: '',
        contactPerson: '',
        contactPhone: '',
        contractAmount: 0,
        completedAmount: 0,
        monthlyFreq: 0,
        currentStock: 0,
        turnoverRate: 0,
        turnoverDays: 0,
        commonCoal: '',
        avgStayTime: 0,
        level: '',
        creditLimit: 0,
        prepayBalance: 0,
        levelReason: '',
        coalDetails: [],
        feeDetails: {},
        monthlyTrend: [],
        coalPref: {},
        railIn: 0,
        truckIn: 0,
        warnings: []
      },
      kpiList: [
        { key: 'contractAmount', label: '合同总量(吨)', formatter: v => (v || v === 0 ? Number(v).toLocaleString() : '--') },
        { key: 'completedAmount', label: '已完成量(吨)', formatter: v => (v || v === 0 ? Number(v).toLocaleString() : '--') },
        { key: 'currentStock', label: '当前库存(吨)', formatter: v => (v || v === 0 ? Number(v).toLocaleString() : '--') },
        { key: 'monthlyFreq', label: '月发运频次(车次)' },
        { key: 'turnoverRate', label: '库存周转率(次/年)' },
        { key: 'turnoverDays', label: '周转天数' },
        { key: 'avgStayTime', label: '平均停留时长(分钟)' },
        { key: 'warningCount', label: '近30天异常次数' }
      ],
      // 图表实例
      feeChart: null,
      monthlyTrendChart: null,
      coalPreferenceChart: null,
      transportCompareChart: null
    }
  },
  computed: {
    contractPercent() {
      const { contractAmount, completedAmount } = this.portrait
      if (!contractAmount) return 0
      const val = ((completedAmount || 0) / contractAmount) * 100
      return Number.isNaN(val) ? 0 : Number(val.toFixed(1))
    },
    levelClass() {
      if (this.portrait.level === '高') return 'level-high'
      if (this.portrait.level === '中') return 'level-mid'
      if (this.portrait.level === '低') return 'level-low'
      return ''
    }
  },
  mounted() {
    this.initCharts()
    this.initCustomerSelect()
  },
  beforeDestroy() {
    this.disposeCharts()
  },
  methods: {
    async initCustomerSelect() {
      try {
        this.selectLoading = true
        const { data } = await getRequset('/api/produce/blade-dept/select/distinct/select?deptName=')
        this.custList = Array.isArray(data) ? data : []
        if (this.custList.length) {
          this.query.custNo = this.custList[0].id
          await this.fetchPortrait()
        }
      } finally {
        this.selectLoading = false
      }
    },
    async remoteMethod(query) {
      if (!query) {
        this.custList = []
        return
      }
      this.selectLoading = true
      try {
        const { data } = await getRequset(`/api/produce/blade-dept/select/distinct/select?deptName=${encodeURIComponent(query)}`)
        this.custList = Array.isArray(data) ? data : []
      } finally {
        this.selectLoading = false
      }
    },
    handleCustomerChange() {
      this.fetchPortrait()
    },
    handleDateChange(value) {
      if (value) {
        this.query.startTime = `${value}-01-01 00:00:00`
        this.query.endTime = `${value}-12-31 23:59:59`
      } else {
        this.query.startTime = ''
        this.query.endTime = ''
      }
      this.fetchPortrait()
    },
    handleRefresh() {
      this.fetchPortrait()
    },
    async fetchPortrait() {
      if (!this.query.custNo) {
        this.portrait = this.defaultPortrait()
        this.clearCharts()
        return
      }
      this.pageLoading = true
      try {
        const { data: { data } } = await getCustomerPortrait({
          custNo: this.query.custNo,
          startTime: this.query.startTime,
          endTime: this.query.endTime,
          businessType: 2
        })
        this.portrait = {
          ...this.defaultPortrait(),
          ...(data || {})
        }
      } catch (error) {
        this.portrait = this.defaultPortrait()
        this.$message.error('客户运输画像数据加载失败')
        this.clearCharts()
      } finally {
        this.pageLoading = false
        this.$nextTick(() => {
          this.disposeCharts()
          this.initCharts()
          this.updateAllCharts()
        })
      }
    },
    defaultPortrait() {
      return {
        customerName: '',
        creditCode: '',
        contactPerson: '',
        contactPhone: '',
        contractAmount: 0,
        completedAmount: 0,
        monthlyFreq: 0,
        currentStock: 0,
        turnoverRate: 0,
        turnoverDays: 0,
        commonCoal: '',
        avgStayTime: 0,
        level: '',
        creditLimit: 0,
        prepayBalance: 0,
        levelReason: '',
        coalDetails: [],
        feeDetails: {},
        monthlyTrend: [],
        monthlyTrendTitles: [],
        coalPref: {},
        railIn: 0,
        truckIn: 0,
        warnings: [],
        warningCount: 0
      }
    },
    initCharts() {
      if (this.$refs.feeChart) {
        this.feeChart = echarts.init(this.$refs.feeChart)
      }
      if (this.$refs.monthlyTrendChart) {
        this.monthlyTrendChart = echarts.init(this.$refs.monthlyTrendChart)
      }
      if (this.$refs.coalPreferenceChart) {
        this.coalPreferenceChart = echarts.init(this.$refs.coalPreferenceChart)
      }
      if (this.$refs.transportCompareChart) {
        this.transportCompareChart = echarts.init(this.$refs.transportCompareChart)
      }
      window.addEventListener('resize', this.handleResize)
    },
    disposeCharts() {
      if (this.feeChart) this.feeChart.dispose()
      if (this.monthlyTrendChart) this.monthlyTrendChart.dispose()
      if (this.coalPreferenceChart) this.coalPreferenceChart.dispose()
      if (this.transportCompareChart) this.transportCompareChart.dispose()
      window.removeEventListener('resize', this.handleResize)
    },
    handleResize() {
      if (this.feeChart) this.feeChart.resize()
      if (this.monthlyTrendChart) this.monthlyTrendChart.resize()
      if (this.coalPreferenceChart) this.coalPreferenceChart.resize()
      if (this.transportCompareChart) this.transportCompareChart.resize()
    },
    updateAllCharts() {
      this.renderFeeChart()
      this.renderMonthlyTrend()
      this.renderCoalPreference()
      this.renderTransportCompare()
    },
    clearCharts() {
      if (this.feeChart) this.feeChart.clear()
      if (this.monthlyTrendChart) this.monthlyTrendChart.clear()
      if (this.coalPreferenceChart) this.coalPreferenceChart.clear()
      if (this.transportCompareChart) this.transportCompareChart.clear()
    },
    renderFeeChart() {
      if (!this.feeChart) return
      const feeData = Object.entries(this.portrait.feeDetails || {}).map(([name, value]) => ({ name, value }))
      this.feeChart.clear()
      this.feeChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 40, right: 10, top: 30, bottom: 40 },
        xAxis: { type: 'category', data: feeData.map(f => f.name), axisLabel: { rotate: 0 } },
        yAxis: { type: 'value', name: '金额(万元)' },
        series: [
          {
            type: 'bar',
            data: feeData.map(f => f.value),
            itemStyle: { color: '#8b5cf6', borderRadius: [6, 6, 0, 0] },
            label: { show: true, position: 'top' }
          }
        ]
      })
    },
    renderMonthlyTrend() {
      if (!this.monthlyTrendChart) return
      const data = this.portrait.monthlyTrend || []
      const months = this.portrait.monthlyTrendTitles && this.portrait.monthlyTrendTitles.length
        ? this.portrait.monthlyTrendTitles
        : []
      this.monthlyTrendChart.clear()
      this.monthlyTrendChart.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 10, top: 30, bottom: 40 },
        xAxis: { type: 'category', data: months },
        yAxis: { type: 'value', name: '发运量(吨)' },
        series: [
          {
            type: 'line',
            data,
            smooth: true,
            lineStyle: { color: '#f97316', width: 2 },
            areaStyle: { opacity: 0.08 },
            symbol: 'circle'
          }
        ]
      })
    },
    renderCoalPreference() {
      if (!this.coalPreferenceChart) return
      const pieData = Object.entries(this.portrait.coalPref || {}).map(([name, value]) => ({ name, value }))
      this.coalPreferenceChart.clear()
      this.coalPreferenceChart.setOption({
        tooltip: { trigger: 'item' },
        legend: { orient: 'vertical', left: 'left', textStyle: { fontSize: 11 } },
        series: [
          {
            type: 'pie',
            radius: '60%',
            center: ['60%', '50%'],
            data: pieData,
            label: { show: true, formatter: '{b}: {d}%', fontSize: 11 }
          }
        ]
      })
    },
    renderTransportCompare() {
      if (!this.transportCompareChart) return
      const contractAmount = Number(this.portrait.contractAmount || 0)
      const completedAmount = Number(this.portrait.completedAmount || 0)
      const yearOutbound = Number(this.portrait.totalOutWeight || 0)
      this.transportCompareChart.clear()
      this.transportCompareChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 55, right: 10, top: 30, bottom: 40 },
        xAxis: { type: 'category', data: ['合同总量', '累计完成量', '本年实际出库'] },
        yAxis: { type: 'value', name: '吨' },
        series: [
          {
            type: 'bar',
            data: [contractAmount, completedAmount, yearOutbound],
            itemStyle: {
              color: params => ['#3b82f6', '#10b981', '#f97316'][params.dataIndex],
              borderRadius: [6, 6, 0, 0]
            },
            label: { show: true, position: 'top' }
          }
        ]
      })
    },
    formatNumber(val) {
      if (!val && val !== 0) return '--'
      const num = Number(val)
      if (Number.isNaN(num)) return '--'
      return num.toLocaleString()
    },
    formatPercent(row) {
      if (!row || !row.plan) return '--'
      const val = (row.actual / row.plan) * 100
      if (Number.isNaN(val)) return '--'
      return `${val.toFixed(1)}%`
    }
  }
}
</script>

<style scoped lang="scss">
.customer-transport-portrait {
  padding: 0;

  .portrait-header {
    align-items: flex-start;
  }

  .filter-bar {
    align-items: flex-end;
    margin: 0;
    column-gap: 16px;

    .el-form-item {
      margin-bottom: 0;
    }

    .date-item {
      min-width: 260px;
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;

    .section-title {
      font-size: 16px;
      font-weight: 600;
      border-left: 3px solid #2563eb;
      padding-left: 10px;
      color: #1f2937;
    }
  }

  .base-info-card {
    margin-bottom: 12px;
  }

  .info-row {
    padding: 10px 16px 4px;
  }

  .info-item {
    margin-bottom: 8px;

    .info-label {
      font-size: 12px;
      color: #6b7280;
      margin-bottom: 4px;
    }

    .info-value {
      font-size: 14px;
      font-weight: 500;
      color: #111827;
    }
  }

  .kpi-section {
    margin-bottom: 12px;

    .kpi-label {
      font-size: 12px;
      color: #6b7280;
      margin-bottom: 6px;
    }

    .kpi-value {
      font-size: 20px;
      font-weight: 600;
      color: #111827;
    }
  }

  .contract-section {
    margin-bottom: 12px;
  }

  .progress-bar-container {
    background: #e5e7eb;
    border-radius: 999px;
    height: 20px;
    overflow: hidden;
    margin: 8px 16px 0;
  }

  .progress-fill {
    background: linear-gradient(90deg, #3b82f6, #60a5fa);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 10px;
    color: #fff;
    font-size: 11px;
    white-space: nowrap;
    transition: width 0.3s ease;
  }

  .sub-title-note {
    font-size: 12px;
    color: #9ca3af;
    margin: 8px 16px 0;
  }

  .sub-card-title {
    font-weight: 500;
    font-size: 14px;
    padding: 8px 0 8px 0;
    margin: 4px 0 4px;
  }

  .transport-section {
    margin-bottom: 12px;
  }

  .value-section {
    margin-bottom: 12px;
  }

  .warning-section {
    margin-bottom: 0;
  }

  .chart-card {
    background: #ffffff;
    padding: 8px 12px 4px;
    box-sizing: border-box;
  }

  /* 煤种偏好中的图容器去掉边框和圆角，保持更轻量的视觉 */
  .value-section .chart-card {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  /* 为合同执行、运输与库存两个大区块增加适度内边距（略微收紧） */
  ::v-deep .contract-section > .el-row,
  ::v-deep .transport-section > .el-row {
    padding: 8px 16px 10px;
    box-sizing: border-box;
  }

  .fade-card,
  .fade-card-group .nx-section-card {
    animation: fade-in-up 0.3s ease-out;
  }

  .level-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
  }

  .level-high {
    background: #fee2e2;
    color: #ef4444;
  }

  .level-mid {
    background: #fef9e3;
    color: #eab308;
  }

  .level-low {
    background: #eefaf3;
    color: #10b981;
  }

  .note {
    font-size: 11px;
    color: #9ca3af;
    text-align: right;
    padding: 4px 16px 8px;
  }

  .reason-item {
    padding: 0 16px 8px;
  }

  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>

